import type { ReactNode } from 'react'
import { cx } from '@/lib/clases'
import { COLOR_PUNTO, type Tono } from './tonos'

/**
 * Insignia de estado — punto de color más texto, **nunca solo el punto**.
 *
 * El color nunca lleva información solo. Quien no distingue verde de rojo lee
 * la palabra; quien sí, la lee de un vistazo por el punto. Las dos cosas a la
 * vez, siempre.
 */

export interface PropsPuntoEstado {
  tono: Tono
  className?: string
}

/** El punto suelto. Solo se usa dentro de una insignia o con texto al lado. */
export function PuntoEstado({ tono, className }: PropsPuntoEstado) {
  return (
    <span
      aria-hidden="true"
      className={cx('inline-block size-2 shrink-0 rounded-full', COLOR_PUNTO[tono], className)}
    />
  )
}

export interface PropsInsigniaEstado {
  tono: Tono
  children: ReactNode
  /** Texto secundario al lado, en gris. "Aceptado condicional · Válido". */
  aclaracion?: string
  /** Descripción larga para lector de pantalla si el texto visible no basta. */
  titulo?: string
  className?: string
}

export function InsigniaEstado({ tono, children, aclaracion, titulo, className }: PropsInsigniaEstado) {
  return (
    <span
      className={cx('inline-flex items-center gap-2 whitespace-nowrap text-cuerpo', className)}
      title={titulo}
    >
      <PuntoEstado tono={tono} />
      <span className="text-gris-900">{children}</span>
      {aclaracion ? <span className="text-etiqueta text-gris-600">{aclaracion}</span> : null}
    </span>
  )
}

/**
 * Insignia discreta de origen o categoría, sin color de estado.
 * Para "migrado", "borrador", "reconstruida" y similares: informan, no alarman.
 */
export function InsigniaDiscreta({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-md border border-gris-300 bg-gris-50 px-1',
        'text-etiqueta text-gris-600',
        className,
      )}
    >
      {children}
    </span>
  )
}
